/**
 * DuplicateTypeModal Component
 * Modal for duplicating a quote type with a new name
 * Extracted from App.js to reduce main file complexity
 */
import React from 'react';
import { Copy, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { formatCurrency } from '@/utils/formatters';

const DuplicateTypeModal = ({
  duplicateType,
  onClose,
  duplicateNewName,
  setDuplicateNewName,
  onDuplicate,
  isDuplicating
}) => {
  const isOpen = !!duplicateType;

  const handleClose = () => {
    onClose(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5" />
            Duplicar Tipo
          </DialogTitle>
          <DialogDescription>
            Crea una copia de &quot;{duplicateType?.name}&quot; con un nuevo nombre
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Original type info */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            {duplicateType?.image_data ? (
              <img 
                src={duplicateType.image_data}
                alt={duplicateType.name}
                className="w-12 h-12 object-contain rounded border"
              />
            ) : (
              <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                <Square className="w-6 h-6 text-slate-400" />
              </div>
            )}
            <div>
              <p className="font-medium text-slate-900">{duplicateType?.name}</p>
              <p className="text-sm text-slate-500">
                {duplicateType?.material} {duplicateType?.series && `- ${duplicateType.series}`}
              </p>
              <p className="text-sm text-emerald-600 font-medium">
                {formatCurrency(duplicateType?.average_cost_per_m2 || 0)}/m²
              </p>
            </div>
          </div>

          {/* New name input */}
          <div className="space-y-2">
            <Label>Nuevo nombre *</Label>
            <Input
              data-testid="duplicate-name-input"
              placeholder="Nombre para el nuevo tipo"
              value={duplicateNewName}
              onChange={(e) => setDuplicateNewName(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-slate-500">
              Se copiará la imagen, material, serie, color y todas las muestras
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            onClick={onDuplicate}
            disabled={isDuplicating || !duplicateNewName.trim()}
            data-testid="duplicate-submit-btn"
          >
            {isDuplicating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Duplicando...
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Duplicar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateTypeModal;
